import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getLocationOccupancy,
  type LocationOccupancyData,
  type LocationOccupancySession,
  type ClubLocation,
} from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameCard, GameIcon } from "../../../components/game";

// Helper to get week dates
function getWeekDates(date: Date): Date[] {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
  const monday = new Date(d.setDate(diff));

  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    dates.push(date);
  }
  return dates;
}

// Helper to format date as ISO string (YYYY-MM-DD)
function formatDateISO(date: Date): string {
  return date.toISOString().split("T")[0];
}

// Generate time slots (7:00 - 22:00)
const TIME_SLOTS = Array.from({ length: 16 }, (_, i) => {
  const hour = i + 7;
  return `${hour.toString().padStart(2, "0")}:00`;
});

export default function AppCoachCalendar() {
  const { t } = useTranslation();
  const { clubs: ctxClubs, activeClubId: ctxClubId } = useAuth();

  const [occupancyData, setOccupancyData] = useState<LocationOccupancyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const activeClubId = ctxClubId || ctxClubs[0]?.id;

  const weekDates = useMemo(() => getWeekDates(currentWeek), [currentWeek]);

  useEffect(() => {
    const loadOccupancy = async () => {
      if (!activeClubId) return;
      setLoading(true);
      try {
        const from = formatDateISO(weekDates[0]);
        const to = formatDateISO(weekDates[6]);
        const data = await getLocationOccupancy(activeClubId, from, `${to}T23:59:59`);
        setOccupancyData(data);

        // Auto-select first location if not selected
        if (!selectedLocation && data.locations.length > 0) {
          setSelectedLocation(data.locations[0].id);
        }
      } catch (error) {
        console.error("Failed to load occupancy:", error);
      }
      setLoading(false);
    };
    loadOccupancy();
  }, [activeClubId, weekDates, selectedLocation]);

  const handlePrevWeek = () => {
    const prev = new Date(currentWeek);
    prev.setDate(prev.getDate() - 7);
    setCurrentWeek(prev);
  };

  const handleNextWeek = () => {
    const next = new Date(currentWeek);
    next.setDate(next.getDate() + 7);
    setCurrentWeek(next);
  };

  const handleToday = () => {
    setCurrentWeek(new Date());
  };

  // Get sessions for selected location
  const locationSessions = useMemo(() => {
    if (!occupancyData || !selectedLocation) return [];
    return occupancyData.occupancyByLocation[selectedLocation] || [];
  }, [occupancyData, selectedLocation]);

  // Get sessions for a specific day
  const getSessionsForDay = (date: Date): LocationOccupancySession[] => {
    const dateStr = formatDateISO(date);
    return locationSessions.filter(s => s.start_at.startsWith(dateStr));
  };

  // Get occupancy level for a day (number of sessions)
  const getDayOccupancy = (date: Date): "free" | "low" | "medium" | "high" => {
    const sessions = getSessionsForDay(date);
    if (sessions.length === 0) return "free";
    if (sessions.length <= 2) return "low";
    if (sessions.length <= 4) return "medium";
    return "high";
  };

  const getOccupancyColor = (level: "free" | "low" | "medium" | "high") => {
    switch (level) {
      case "free": return "bg-emerald-100 text-emerald-700";
      case "low": return "bg-blue-100 text-blue-700";
      case "medium": return "bg-amber-100 text-amber-700";
      case "high": return "bg-red-100 text-red-700";
    }
  };

  const selectedDaySessions = selectedDay ? getSessionsForDay(selectedDay) : [];
  const selectedLocationDetails = occupancyData?.locations.find(l => l.id === selectedLocation);

  return (
    <MobileAppLayout>
      <div className="px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-extrabold text-slate-800">{t("coach.calendar", "Calendar locații")}</h1>
        </div>

        {/* Location Selector */}
        {occupancyData && occupancyData.locations.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
            {occupancyData.locations.map(location => (
              <button
                key={location.id}
                onClick={() => setSelectedLocation(location.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-2xl text-sm font-bold transition-colors ${
                  selectedLocation === location.id
                    ? "bg-gradient-to-b from-indigo-500 to-blue-600 text-white shadow-[0_3px_0_#3730a3]"
                    : "bg-white text-slate-600 border border-slate-200 shadow-[0_2px_0_#eef2f8] hover:bg-slate-50"
                }`}
              >
                {location.name}
              </button>
            ))}
          </div>
        )}

        {/* Week Navigation */}
        <GameCard className="p-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevWeek}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="text-center">
              <p className="font-semibold text-slate-800">
                {weekDates[0].toLocaleDateString("ro-RO", { month: "long", year: "numeric" })}
              </p>
              <button
                onClick={handleToday}
                className="text-xs text-indigo-600 font-medium hover:text-indigo-700"
              >
                {t("coach.today", "Azi")}
              </button>
            </div>
            <button
              onClick={handleNextWeek}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Week Days Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-2">
              {weekDates.map((date, index) => {
                const isToday = formatDateISO(date) === formatDateISO(new Date());
                const isSelected = selectedDay && formatDateISO(date) === formatDateISO(selectedDay);
                const occupancy = getDayOccupancy(date);
                const dayNames = ["L", "M", "M", "J", "V", "S", "D"];

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDay(date)}
                    className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                      isSelected
                        ? "bg-indigo-500 text-white shadow-md scale-105"
                        : isToday
                        ? "bg-indigo-100 text-indigo-700"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <span className="text-xs font-medium mb-1">{dayNames[index]}</span>
                    <span className={`text-lg font-bold ${isSelected ? "" : isToday ? "" : "text-slate-800"}`}>
                      {date.getDate()}
                    </span>
                    <div className={`w-6 h-1.5 rounded-full mt-1 ${
                      isSelected ? "bg-white/50" : getOccupancyColor(occupancy).replace("text-", "bg-").split(" ")[0]
                    }`}></div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-emerald-100"></div>
              <span className="text-xs text-slate-500">{t("coach.free", "Liber")}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-blue-100"></div>
              <span className="text-xs text-slate-500">{t("coach.low", "Puțin")}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-amber-100"></div>
              <span className="text-xs text-slate-500">{t("coach.medium", "Mediu")}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-100"></div>
              <span className="text-xs text-slate-500">{t("coach.high", "Ocupat")}</span>
            </div>
          </div>
        </GameCard>

        {/* Selected Day Sessions */}
        {selectedDay && (
          <GameCard className="overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-bold text-slate-800">
                {selectedDay.toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" })}
              </h2>
              <button
                onClick={() => setSelectedDay(null)}
                className="p-1 hover:bg-slate-200 rounded-full"
              >
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {selectedDaySessions.length === 0 ? (
              <div className="p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-2 bg-emerald-50 rounded-2xl flex items-center justify-center">
                  <GameIcon name="check" className="w-6 h-6 text-emerald-500" />
                </div>
                <p className="text-slate-500">{t("coach.noSessionsOnDay", "Nicio sesiune programată")}</p>
                <p className="text-emerald-600 text-sm font-medium mt-1">{selectedLocationDetails?.name} - {t("coach.available", "Disponibil")}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {selectedDaySessions
                  .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime())
                  .map(session => (
                    <div key={session.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-full rounded-full ${
                          session.type === "sport" ? "bg-emerald-500" : "bg-purple-500"
                        }`}></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">
                              {new Date(session.start_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                              {" - "}
                              {new Date(session.end_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              session.type === "sport" ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-700"
                            }`}>
                              {session.type === "sport" ? "Sport" : "Educațional"}
                            </span>
                          </div>
                          <h4 className="font-medium text-slate-800 mt-1">{session.title}</h4>
                          <p className="text-xs text-slate-500 mt-0.5">{session.team?.name}</p>
                          {session.coach && (
                            <p className="text-xs text-slate-400">
                              {t("coach.coach", "Antrenor")}: {session.coach.full_name || session.coach.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </GameCard>
        )}

        {/* Location Info */}
        {selectedLocationDetails && !selectedDay && (
          <GameCard className="p-4">
            <h3 className="font-bold text-slate-800 mb-3">{selectedLocationDetails.name}</h3>
            {selectedLocationDetails.address && (
              <div className="flex items-start gap-2 text-sm text-slate-500 mb-2">
                <svg className="w-4 h-4 mt-0.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <span>{selectedLocationDetails.address}</span>
              </div>
            )}
            {selectedLocationDetails.capacity && (
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t("coach.capacity", "Capacitate")}: {selectedLocationDetails.capacity}</span>
              </div>
            )}
            {selectedLocationDetails.description && (
              <p className="text-sm text-slate-500 mt-2">{selectedLocationDetails.description}</p>
            )}
          </GameCard>
        )}

        {/* Empty State */}
        {!loading && occupancyData?.locations.length === 0 && (
          <GameCard className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <p className="text-slate-500">{t("coach.noLocations", "Nicio locație configurată")}</p>
            <p className="text-slate-400 text-sm mt-1">{t("coach.contactAdmin", "Contactează administratorul clubului")}</p>
          </GameCard>
        )}
      </div>
    </MobileAppLayout>
  );
}
